import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  LinearProgress,
  TextField,
  Autocomplete,
  TablePagination,
} from '@mui/material';
import {
  Edit as EditIcon,
  Security as PiiIcon,
  Key as KeyIcon,
  Link as ForeignKeyIcon,
} from '@mui/icons-material';
import { UnifiedColumnData, ColumnSummary, SEMANTIC_ROLES } from '../../types/unifiedSchema';
import { isColumnInList } from '../../hooks/useUnifiedSchema';
import {
  findColumnPathInStructure,
  hasAnyLevel,
  type OutputStructure,
} from '../../lib/outputStructure';

interface UnifiedColumnTableProps {
  columns: UnifiedColumnData[];
  primaryKeyCandidates: string[];
  foreignKeyCandidates: string[];
  tableIndex: number;
  onUpdateColumn: (tableIndex: number, colIndex: number, updates: Partial<ColumnSummary>) => void;
  onOpenColumnEditor: (tableIndex: number, colIndex: number, column: ColumnSummary) => void;
  /** When present, shows a "Hierarchy Position" column with each column's position in the output structure. */
  outputStructure?: Record<string, any> | null;
}

const UnifiedColumnTable: React.FC<UnifiedColumnTableProps> = ({
  columns,
  primaryKeyCandidates,
  foreignKeyCandidates,
  tableIndex,
  onUpdateColumn,
  onOpenColumnEditor,
  outputStructure,
}) => {
  const showHierarchy = hasAnyLevel(outputStructure as unknown as OutputStructure);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const visibleColumns = columns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Null %</TableCell>
              <TableCell align="center">Unique</TableCell>
              <TableCell>Semantic Role</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">PII</TableCell>
              <TableCell align="center">Keys</TableCell>
              {showHierarchy && <TableCell>Hierarchy Position</TableCell>}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleColumns.map((col, pageIdx) => {
              const colIndex = page * rowsPerPage + pageIdx;
              return (
                <TableRow key={`${col.name}-${colIndex}`} hover>
                  <TableCell>
                    <Tooltip
                      title={
                        col.sample_values.length > 0
                          ? `Samples: ${col.sample_values.slice(0, 3).join(', ')}`
                          : ''
                      }
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {col.original_name || col.name}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Chip label={col.data_type} size="small" variant="outlined" />
                  </TableCell>

                  <TableCell align="center" sx={{ minWidth: 80 }}>
                    <Stack spacing={0.25} alignItems="center">
                      <Typography variant="caption">
                        {col.null_percentage.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={col.null_percentage}
                        color={
                          col.null_percentage > 50
                            ? 'error'
                            : col.null_percentage > 20
                            ? 'warning'
                            : 'success'
                        }
                        sx={{ width: '100%', height: 4, borderRadius: 2 }}
                      />
                    </Stack>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2">
                      {col.unique_count.toLocaleString()}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ minWidth: 150 }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={SEMANTIC_ROLES}
                      value={col.semantic_role || 'unknown'}
                      onChange={(_, newValue) => {
                        onUpdateColumn(tableIndex, colIndex, {
                          semantic_role: (newValue || 'unknown').toLowerCase().replace(/\s+/g, '_'),
                        });
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') {
                          onUpdateColumn(tableIndex, colIndex, {
                            semantic_role: newInputValue.toLowerCase().replace(/\s+/g, '_'),
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="small" />
                      )}
                      getOptionLabel={(option) => option.replace(/_/g, ' ')}
                      disableClearable
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 200, maxWidth: 320 }}>
                    <Tooltip
                      title={col.description || ''}
                      placement="top"
                      enterDelay={500}
                      disableHoverListener={!col.description || col.description.length < 30}
                    >
                      <TextField
                        size="small"
                        value={col.description || ''}
                        onChange={(e) =>
                          onUpdateColumn(tableIndex, colIndex, {
                            description: e.target.value,
                          })
                        }
                        placeholder="Add description..."
                        variant="standard"
                        multiline
                        maxRows={2}
                        fullWidth
                      />
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    {col.pii_likelihood > 0.5 ? (
                      <Tooltip title={`PII: ${(col.pii_likelihood * 100).toFixed(0)}%`}>
                        <Chip icon={<PiiIcon />} label="PII" size="small" color="warning" />
                      </Tooltip>
                    ) : col.pii_likelihood > 0.2 ? (
                      <Tooltip title={`PII: ${(col.pii_likelihood * 100).toFixed(0)}%`}>
                        <Chip label="Low" size="small" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {(col.is_primary_key ||
                        isColumnInList(col.name, primaryKeyCandidates)) && (
                        <Tooltip title="Primary Key">
                          <KeyIcon fontSize="small" color="success" />
                        </Tooltip>
                      )}
                      {(col.is_foreign_key ||
                        isColumnInList(col.name, foreignKeyCandidates)) && (
                        <Tooltip
                          title={`Foreign Key${col.fk_references ? `: ${col.fk_references}` : ''}`}
                        >
                          <ForeignKeyIcon fontSize="small" color="info" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>

                  {showHierarchy && (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          // Walk the tree to locate the column at any depth.
                          const hit = findColumnPathInStructure(
                            outputStructure as unknown as OutputStructure,
                            col.name,
                          );
                          if (!hit) return '-';
                          const path = hit.path.join(' ▸ ');
                          return hit.role === 'key'
                            ? `Key at ${path}`
                            : `Field at ${path}`;
                        })()}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell align="center">
                    <Tooltip title="Edit Column Details">
                      <IconButton
                        size="small"
                        onClick={() =>
                          onOpenColumnEditor(tableIndex, colIndex, {
                            name: col.name,
                            original_name: col.original_name,
                            semantic_role: col.semantic_role,
                            description: col.description,
                            pii_likelihood: col.pii_likelihood,
                            is_primary_key: col.is_primary_key,
                            is_foreign_key: col.is_foreign_key,
                            fk_references: col.fk_references,
                            data_type: col.data_type,
                          })
                        }
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {columns.length > 25 && (
        <TablePagination
          component="div"
          sx={{
            '& .MuiTablePagination-actions': {
              mr: { xs: 2, sm: 2 },
            },
            '& .MuiTablePagination-displayedRows': {
              mr: 1,
            },
          }}
          count={columns.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </>
  );
};

export default UnifiedColumnTable;
